// LAT-934: pure types + helpers shared between the Guardian panel (`quality-guard-entity-state.tsx`)
// and the aspect-first finding card (`finding-card.tsx`). Extracted so the card can be its own
// file without a circular import back into the panel. No React and no Convex client imports —
// `api`, `Doc` and `Id` are type-only, read for the shapes the queries send, and `humanizeKey`
// is a pure string helper.

import type { api } from "@repo/convex/api";
import type { Doc, Id } from "@repo/convex/dataModel";
import {
  deriveQualityGuardReadinessLabel,
  humanizeKey,
  isCountedQualityGuardIssue,
  isCoveredQualityGuardFinding,
} from "@repo/shared";
import type { FunctionReturnType } from "convex/server";

export type QualityGuardCriticality = "critical" | "high" | "recommended" | "optional" | "info";
export type QualityGuardReadinessLabel =
  | "checking"
  | "unready"
  | "needs_attention"
  | "unsafe"
  | "accepted_risk"
  | "ready";
export type QualityGuardAxis = "completeness" | "quality";
/**
 * The axes a panel row can carry. `openWork` is QG2's alone — a commitment carried
 * forward from an earlier judgment — and keeps its own group rather than being folded
 * into quality, which would report it as a defect in what is written.
 */
export type QualityGuardPanelAxis = QualityGuardAxis | "openWork";
/**
 * Which engine produced a row. Absent means the incumbent's: its rows carry no engine
 * field, and adding one to them would change a payload the flag-off contract pins.
 */
export type QualityGuardEngine = "quality_guard" | "quality_guard_2";
export type QualityGuardRemediationRoute =
  | "none"
  | "claudia_change_candidate"
  | "claudia_clarification"
  | "accept_risk";

export type QualityGuardEvaluationState = "complete" | "partial" | "unchecked" | "not_applicable";
export interface QualityGuardEvaluationCounts {
  judged: number;
  notApplicable: number;
  unverified: number;
  errored: number;
}

export type QualityGuardProjection = Doc<"qualityGuardReadinessProjections"> & {
  readinessLabel: QualityGuardReadinessLabel;
  // LAT-858 P5: optional, frontend-declared integration seam — the backend contract that adds
  // these to `qualityGuardReadinessProjections` rows (entity and section scope alike) lands in a
  // parallel stream. Absent means legacy "complete" behavior (no visual change).
  evaluationState?: QualityGuardEvaluationState;
  evaluationCountsJson?: QualityGuardEvaluationCounts;
};
/**
 * What the summary header actually reads off a projection — the fields both engines
 * emit, plus the incumbent-only ones the header degrades gracefully without.
 *
 * The header renders whichever engine is of record (seam §4.1), and QG2's rows carry no
 * per-rule evaluation counts, so those stay optional here rather than being invented on
 * the QG2 side. A full `QualityGuardProjection` satisfies this, which is what keeps the
 * incumbent path unchanged.
 */
export type QualityGuardHeaderProjection = {
  /**
   * Which engine wrote this row. Absent means the incumbent's. It is read for one fact
   * only: a QG2 row exists only once a check settled, so its presence is the "we actually
   * checked this" the incumbent answers with `completedRuleCount`, a rule count QG2 has
   * no counterpart for.
   */
  engine?: QualityGuardEngine;
  readinessLabel: QualityGuardReadinessLabel;
  checking: boolean;
  openFindingCount: number;
  openSevereFindingCount: number;
  acceptedRiskCount: number;
  completenessIssueCount?: number;
  completenessSeverity?: "none" | "yellow" | "red";
  completenessHighestCriticality?: string;
  qualityIssueCount?: number;
  qualitySeverity?: "none" | "yellow" | "red";
  qualityHighestCriticality?: string;
  criticalIssueCount?: number;
  highIssueCount?: number;
  recommendedIssueCount?: number;
  completedRuleCount?: number;
  evaluationState?: QualityGuardEvaluationState;
  evaluationCountsJson?: QualityGuardEvaluationCounts;
  qualityApplicable?: boolean;
};

export type QualityGuardSectionProjection = {
  fieldId: string;
  tabKey?: string | null;
  projection: QualityGuardHeaderProjection;
};
/**
 * One open issue, from whichever engine is of record.
 *
 * The two members carry the same field names — that is the whole point of the backend
 * seam — and are a union rather than one widened shape because two things genuinely
 * differ: which table the id addresses, and therefore which mutation an action calls.
 * `engine` is the discriminant, and it is absent on the incumbent's rows so the flag-off
 * payload is unchanged.
 */
export type QualityGuardIncumbentFinding = Doc<"qualityGuardFindings"> & {
  axis: QualityGuardAxis;
  criticality: QualityGuardCriticality;
  engine?: undefined;
  acceptKey?: undefined;
  rowKey?: undefined;
  needsReview?: undefined;
};
/**
 * One row of the entity panel's QG2 reading, derived from the query that sends it.
 *
 * Not restated here: a copy of a projection is what lets a renamed or dropped field keep
 * compiling while the panel reads `undefined`. `rowKey` in particular is required on this
 * arm — a finding with three open accept keys is three cards carrying one `_id`, so the id
 * names all three and tells none of them apart — and the requirement is the backend's own,
 * read off the query rather than asserted alongside it.
 *
 * One field the row carries is read by nothing here and that is deliberate. `needsReview`
 * records that the span offered as proof of a gap could not be found, or that a second model
 * answered that the element IS addressed; neither outcome changes the verdict, both travel to
 * the guardian state Claudia reads (`qualityGuard/api.ts`), and the panel draws neither,
 * because its cards say exactly what the incumbent's say whichever engine answered.
 */
export type QualityGuardQg2Finding = Extract<
  FunctionReturnType<typeof api.qualityGuard.getEntityState>["findings"][number],
  { engine: "quality_guard_2" }
>;

export type QualityGuardFinding = QualityGuardIncumbentFinding | QualityGuardQg2Finding;

/**
 * What identifies one row of the finding list.
 *
 * An incumbent row is one finding and carries no `rowKey`, so its id is its identity. A
 * QG2 row is one accept key of a finding and carries its own.
 */
export function qualityGuardFindingRowKey(finding: QualityGuardFinding): string {
  // Read off the derived arm rather than through a fallback: dropped or renamed on the
  // backend, this stops compiling instead of quietly answering with the finding id and
  // collapsing every sibling card of a multi-key finding into one.
  return finding.engine === "quality_guard_2" ? finding.rowKey : String(finding._id);
}
/**
 * Why this issue's Accept-risk control is closed, or `null` when it is open.
 *
 * Both sentences describe a server refusal, not a client rule: `qualityGuard2/
 * acceptance.ts` rejects a finding marked unsafe and a key the expectation document
 * authors at the top tier, and the control states the reason rather than letting the
 * click fail.
 *
 * The two refusals read different things, exactly as the server does. Unsafe is the
 * finding's own readiness impact. The top tier is the KEY's authored severity — the
 * finding's `criticality` is a maximum over every key the row carries, so reading it
 * here would close the control on an ordinary key riding beside a critical one, which
 * the write path accepts. The finding's own value stands only where the key's is
 * absent, which is the fallback `acceptKeyIsCritical` takes for the same reason.
 *
 * The top-tier sentence is asked only of a QG2 row — the incumbent has no such rule, and
 * a control that closed on its findings too would be this panel changing under a flag it
 * is not meant to reveal.
 */
export function acceptRiskRefusalReason(finding: QualityGuardFinding): string | null {
  if (finding.readinessImpact === "unsafe") {
    return "Unsafe findings must be fixed before they can clear readiness.";
  }
  if (finding.engine !== "quality_guard_2") {
    return null;
  }
  const keyCriticality = finding.acceptKeyCriticality ?? finding.criticality;
  if (keyCriticality === "critical") {
    return "The highest-severity findings must be fixed \u2014 they cannot be accepted as risk.";
  }
  return null;
}

export type QualityGuardFindingRemediation = {
  reason?: string;
  criteriaNotMet: string[];
  requiredFix?: string;
  remediationRoute?: QualityGuardRemediationRoute;
  clarificationQuestion?: string;
};
type QualityGuardAcceptedRiskFields = {
  canonicalKey: string;
  familyKey?: string | null;
  aspectId?: string | null;
  criticality: QualityGuardCriticality;
  reason?: string | null;
  acceptedBy?: Id<"users"> | string | null;
  acceptedAt: number;
  expiresAt: number | null;
  findingTitle: string;
  fieldId: string | null;
  tabKey: string | null;
  /** The impact of the finding this covers, absent on a row whose finding could not be read. */
  readinessImpact?: Doc<"qualityGuardFindings">["readinessImpact"] | null;
  /** The axis it was recorded on, which with the scope addresses the finding it covers. */
  axis?: QualityGuardPanelAxis | null;
};
/** A standing acceptance, discriminated the same way a finding is and for the same reason. */
export type QualityGuardAcceptedRisk = QualityGuardAcceptedRiskFields &
  (
    | {
        engine?: undefined;
        acceptedRiskId: Id<"qualityGuardAcceptedRisks">;
        findingId: Id<"qualityGuardFindings">;
      }
    | {
        engine: "quality_guard_2";
        acceptedRiskId: Id<"qualityGuard2AcceptedRisks">;
        findingId: string;
      }
  );
/**
 * LAT-934 founder decision (2026-08-24, reversing the 2026-08-23 aspect-selection points 3-5):
 * there is no aspect-level selection, so risk acceptance always targets the whole finding.
 */
export type QualityGuardRiskTarget = {
  finding: QualityGuardFinding;
};

export const severityRank: Record<QualityGuardCriticality, number> = {
  critical: 0,
  high: 1,
  recommended: 2,
  optional: 3,
  info: 4,
};

const qualityGuardRemediationRoutes = new Set<QualityGuardRemediationRoute>([
  "none",
  "claudia_change_candidate",
  "claudia_clarification",
  "accept_risk",
]);

function getRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function getOptionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : undefined;
}

function getRemediationRoute(value: unknown): QualityGuardRemediationRoute | undefined {
  return qualityGuardRemediationRoutes.has(value as QualityGuardRemediationRoute)
    ? (value as QualityGuardRemediationRoute)
    : undefined;
}

function getStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string" && entry.trim().length > 0)
    : [];
}

export function getFindingRemediation(
  finding: QualityGuardFinding
): QualityGuardFindingRemediation | null {
  const payload = getRecord(finding.remediationCandidatesJson);
  const primary = getRecord(payload.primary);
  const claudia = getRecord(primary.claudia);
  const criteriaNotMet = getStringArray(primary.criteriaNotMet);
  const remediation: QualityGuardFindingRemediation = {
    reason: getOptionalString(primary.reason),
    criteriaNotMet,
    requiredFix: getOptionalString(primary.requiredFix),
    remediationRoute: getRemediationRoute(claudia.remediationRoute),
    clarificationQuestion: getOptionalString(claudia.clarificationQuestion),
  };

  if (
    !remediation.reason &&
    remediation.criteriaNotMet.length === 0 &&
    !remediation.requiredFix &&
    !remediation.remediationRoute &&
    !remediation.clarificationQuestion
  ) {
    return null;
  }

  return remediation;
}

export type QualityGuardFindingSectionLabels = {
  tabLabel?: string | null;
  fieldLabel?: string | null;
};

/**
 * The finding's "section" for display — its resolved field label when known, else the
 * humanized fieldId, else the resolved tab label, else a generic fallback. Shared by the card
 * title (completeness pattern) and the breadcrumb below.
 */
export function resolveFindingSectionLabel(
  finding: QualityGuardFinding,
  labels: QualityGuardFindingSectionLabels
): string {
  const fieldLabel = labels.fieldLabel?.trim();
  if (fieldLabel) {
    return fieldLabel;
  }
  if (finding.fieldId?.trim()) {
    return humanizeKey(finding.fieldId);
  }
  const tabLabel = labels.tabLabel?.trim();
  if (tabLabel) {
    return tabLabel;
  }
  return "This entity";
}

/**
 * LAT-934 founder decisions (point 2, 2026-08-23 preview review): the card title is a
 * section-state SENTENCE, not the raw finding title.
 *
 * - Completeness: fixed template `"<section> is incomplete"` — always, regardless of what the
 *   finding's own `title` says.
 * - Quality: the finding's own authored `title` when non-empty — rule authoring already phrases
 *   quality-axis titles as section-state sentences (the founder's own example, "Business Context
 *   contradicts itself", is a real finding title, not synthesized copy). Falls back to
 *   `"<section> has quality issues"` only when there is no title to use.
 *
 * The fallback is a defensive path, not a commonly-hit one: `qualityGuardFindings.title` is a
 * required, non-optional schema field, so a real quality finding should always have SOME title.
 * Implemented anyway per the founder's explicit "if no natural derivation exists" clause.
 */
export function resolveFindingCardTitle(
  finding: QualityGuardFinding,
  labels: QualityGuardFindingSectionLabels
): string {
  const sectionLabel = resolveFindingSectionLabel(finding, labels);
  if (finding.axis === "completeness") {
    return `${sectionLabel} is incomplete`;
  }
  const authoredTitle = finding.title?.trim();
  if (authoredTitle) {
    return authoredTitle;
  }
  // The same "if no natural derivation exists" fallback, per axis — an open-work item is a
  // commitment still outstanding, not a defect in what is written.
  return finding.axis === "openWork"
    ? `${sectionLabel} has open work`
    : `${sectionLabel} has quality issues`;
}

/**
 * LAT-934 founder decisions (point 7): the breadcrumb/meta line must show friendly display
 * labels ("Business Scope › Initiative Overview"), never raw framework keys
 * ("Dataset.Structure › KeyJoinsAndIdentifiers"). Prefers the resolved tab/field labels (from
 * the compiled framework structure, via `resolveEntityAuthoringTabDescriptors` in the panel);
 * humanizes the raw key only as a last resort, while the real structure is still loading.
 */
export function resolveFindingCardLocation(
  finding: QualityGuardFinding,
  labels: QualityGuardFindingSectionLabels
): string {
  const tabLabel = labels.tabLabel?.trim() || (finding.tabKey ? humanizeKey(finding.tabKey) : null);
  const fieldLabel =
    labels.fieldLabel?.trim() || (finding.fieldId ? humanizeKey(finding.fieldId) : null);
  const parts = [tabLabel, fieldLabel].filter((part): part is string => Boolean(part));

  return parts.length > 0 ? parts.join(" › ") : "Entity";
}

export function sortFindings(findings: readonly QualityGuardFinding[]): QualityGuardFinding[] {
  return [...findings].sort((left, right) => {
    const severityDelta = severityRank[left.criticality] - severityRank[right.criticality];
    if (severityDelta !== 0) {
      return severityDelta;
    }
    return right.updatedAt - left.updatedAt;
  });
}

/**
 * A header reading taken from the issues themselves, for an element whose roll-up row is not
 * there yet.
 *
 * The roll-up is a row of its own, written by the aggregate that folds the issues; the issues
 * are written when a judgment settles. So an element can carry current issues while the row
 * that counts them is still pending, and a header that reads only the row then says nothing
 * has been produced while the list beneath it shows four issues. Everything here is a fact
 * about the issues in hand — their axes, their weights, the word the platform's own ladder
 * reads them by — and nothing is invented: the per-axis `severity` fields stay absent, so the
 * badges read the criticality they always fall back to.
 *
 * `null` when there is nothing to read, which is the state the header already has copy for.
 */
export function deriveHeaderProjectionFromFindings(
  findings: readonly QualityGuardFinding[],
  context: {
    checking: boolean;
    /** The standing acceptances in scope, grouped here into the findings they cover. */
    acceptedRisks?: readonly QualityGuardAcceptedRisk[];
  }
): QualityGuardHeaderProjection | null {
  if (findings.length === 0) {
    return null;
  }
  // The same exclusion the roll-up makes, so the numbers do not change when the row lands:
  // an `info` impact and the two lightest criticalities are listed and never counted, and
  // open work is counted beside the issue axes rather than into them.
  const counted = findings.filter(isCountedQualityGuardIssue);
  const issues = counted.filter((finding) => finding.axis !== "openWork");
  const onAxis = (axis: QualityGuardPanelAxis) => issues.filter((finding) => finding.axis === axis);
  const heaviest = (rows: readonly QualityGuardFinding[]): QualityGuardCriticality | undefined =>
    (["critical", "high", "recommended", "optional", "info"] as const).find((criticality) =>
      rows.some((finding) => finding.criticality === criticality)
    );
  const countOf = (criticality: QualityGuardCriticality) =>
    issues.filter((finding) => finding.criticality === criticality).length;
  // The ladder is the platform's, and the rows it is asked about are not the same rows:
  // `unsafe` reads every issue in hand, counted or not, because an issue can be too light
  // to reach a badge and still stop the scope being relied on. Everything below it reads
  // the counted set.
  // Coverage is a fact about a finding, not about the acceptance list being non-empty: a
  // finding with one key accepted and another still open is one the reader still owes
  // something on, however light the open key is. An issue row exists for every open key, so
  // an acceptance is covered exactly when no open row answers to the same issue.
  //
  // Matched by scope and axis, never by the finding id the acceptance recorded: a re-scan
  // supersedes the finding and mints a new one, so that id names a row nobody lists any
  // more while the keys it was accepted against are open on the replacement.
  //
  // Counted per FINDING, which is what the roll-up counts: a finding whose three keys were
  // each accepted is three acceptance rows and one covered finding, and reporting three
  // would make the Accepted badge drop to one the moment the row lands.
  //
  // Scope and axis address the finding, and `familyKey` tells two of them apart where one
  // scope carries more than one. An incumbent acceptance carries the family of the finding it
  // covers, so several rule families failing on one field and axis count once each. A QG2
  // acceptance carries none — QG2 mints one finding per view and axis, and every acceptance at
  // that scope is one of its keys — so the scope alone is that row's whole identity.
  const coveredFindings = new Set<string>();
  for (const risk of context.acceptedRisks ?? []) {
    const covered = isCoveredQualityGuardFinding({
      hasOpenKey: findings.some((finding) => riskSharesFindingScope(risk, finding)),
      hasAcceptedKey: true,
      readinessImpact: risk.readinessImpact,
    });
    if (covered) {
      coveredFindings.add(
        `${risk.axis ?? ""}::${risk.tabKey ?? ""}::${risk.fieldId ?? ""}::${risk.familyKey ?? ""}`
      );
    }
  }
  const acceptedRiskCount = coveredFindings.size;
  const readinessLabel = deriveQualityGuardReadinessLabel({
    current: findings,
    counted: issues,
    hasAcceptedRisk: acceptedRiskCount > 0,
  });

  const completeness = onAxis("completeness");
  const quality = onAxis("quality");
  const criticalIssueCount = countOf("critical");
  const highIssueCount = countOf("high");
  return {
    readinessLabel: context.checking ? "checking" : readinessLabel,
    checking: context.checking,
    openFindingCount: issues.length,
    openSevereFindingCount: criticalIssueCount + highIssueCount,
    acceptedRiskCount,
    completenessIssueCount: completeness.length,
    ...(heaviest(completeness) ? { completenessHighestCriticality: heaviest(completeness) } : {}),
    qualityIssueCount: quality.length,
    ...(heaviest(quality) ? { qualityHighestCriticality: heaviest(quality) } : {}),
    criticalIssueCount,
    highIssueCount,
    recommendedIssueCount: countOf("recommended"),
  };
}

export function findingMatchesSection(
  finding: QualityGuardFinding,
  section: { tabKey?: string | null; fieldId?: string | null }
): boolean {
  if (!section.fieldId) {
    return true;
  }
  if (finding.fieldId !== section.fieldId) {
    return false;
  }
  return !section.tabKey || !finding.tabKey || finding.tabKey === section.tabKey;
}

export function riskMatchesSection(
  risk: QualityGuardAcceptedRisk,
  section: { tabKey?: string | null; fieldId?: string | null }
): boolean {
  if (!section.fieldId) {
    return true;
  }
  if (risk.fieldId !== section.fieldId) {
    return false;
  }
  return (risk.tabKey ?? null) === (section.tabKey ?? null);
}

/**
 * Whether one open issue row belongs to the finding an acceptance was recorded against.
 *
 * A scope carries one finding per axis, so the scope and the axis address the finding —
 * never the finding id the acceptance stored, which a re-scan supersedes while the keys it
 * covers live on in the replacement. Coverage is a fact about the finding and not about the
 * one key: a sibling key still open leaves the whole finding owing something.
 *
 * Each half is compared only where both sides name it, the same defensiveness
 * {@link riskMatchesFinding} applies to the tab: a row that names no axis matches on scope.
 */
function riskSharesFindingScope(
  risk: QualityGuardAcceptedRisk,
  finding: QualityGuardFinding
): boolean {
  if (risk.fieldId !== (finding.fieldId ?? null)) {
    return false;
  }
  if (risk.tabKey && finding.tabKey && risk.tabKey !== finding.tabKey) {
    return false;
  }
  return !risk.axis || risk.axis === finding.axis;
}

export function riskMatchesFinding(
  risk: QualityGuardAcceptedRisk,
  finding: QualityGuardFinding
): boolean {
  if ((risk.familyKey ?? risk.canonicalKey) !== finding.canonicalKey) {
    return false;
  }
  if (risk.fieldId !== (finding.fieldId ?? null)) {
    return false;
  }
  if (risk.tabKey && finding.tabKey && risk.tabKey !== finding.tabKey) {
    return false;
  }
  return true;
}

// --- LAT-934: aspect-first grouping --------------------------------------------------------
//
// Three groups ship now (Completeness / Quality / Accepted — the founder's 2026-08-23 preview
// review kept these as the design's original axis names rather than the "Incomplete"/"Poor
// quality" rewrite an earlier round tried). Product has named a fourth for later ("Needs
// confirmation" — see specs/LAT-934-guardian-panel-redesign/PLAN.md) that waits on a third axis
// and is deliberately NOT added yet. Adding it later is: append a `{ key, label, matches }` entry
// to `QUALITY_GUARD_OPEN_FINDING_GROUPS` below (or a sibling list, for a group keyed off
// something other than a finding predicate) — the render loop in `quality-guard-entity-state.tsx`
// iterates this array and needs no changes.

export type QualityGuardOpenFindingGroupKey = "completeness" | "quality" | "openWork";

export interface QualityGuardOpenFindingGroupDef {
  key: QualityGuardOpenFindingGroupKey;
  label: string;
  matches: (finding: QualityGuardFinding) => boolean;
}

export const QUALITY_GUARD_OPEN_FINDING_GROUPS: readonly QualityGuardOpenFindingGroupDef[] = [
  {
    key: "completeness",
    label: "Completeness",
    matches: (finding) => finding.axis === "completeness",
  },
  { key: "quality", label: "Quality", matches: (finding) => finding.axis === "quality" },
  // QG2's third axis. Empty groups are dropped below, so this adds nothing to a panel the
  // incumbent is of record for — no incumbent finding carries this axis.
  { key: "openWork", label: "Open work", matches: (finding) => finding.axis === "openWork" },
];

export interface QualityGuardOpenFindingGroup extends QualityGuardOpenFindingGroupDef {
  items: QualityGuardFinding[];
}

/** Buckets already-sorted open findings into the ordered group defs, dropping empty groups. */
export function groupOpenFindings(
  findings: readonly QualityGuardFinding[]
): QualityGuardOpenFindingGroup[] {
  return QUALITY_GUARD_OPEN_FINDING_GROUPS.map((group) => ({
    ...group,
    items: findings.filter(group.matches),
  })).filter((group) => group.items.length > 0);
}

/** The "Accepted" group's label — kept alongside the open-group labels for one consistent header style. */
export const QUALITY_GUARD_ACCEPTED_GROUP_LABEL = "Accepted";
